import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, createBlock as _createBlock, toDisplayString as _toDisplayString, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives, Transition as _Transition, withCtx as _withCtx, normalizeStyle as _normalizeStyle, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-d6b8eee4"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "no_data"
}
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", null, null, -1))
const _hoisted_3 = [
  _hoisted_2
]
const _hoisted_4 = ["onClick"]

import APP from '@/js/const/app'
import Icon from '@/components/atoms/Icon'
import ResizeBox from '@/components/atoms/ResizeBox'
import { deepCopy, range } from '@/js/utils/util'
import live from '@/js/utils/live'
import MultiVideoLive from './MultiVideoLive'
import MultiVideoArchive from './MultiVideoArchive'
import MultiVideoTimelapse from './MultiVideoTimelapse'
import MultiVideoStream from './MultiVideoStream'

import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
  withDefaults,
} from 'vue'
import { CameraDetail } from '@/@types/typescript-axios'
import { useRouter } from 'vue-router'

// videoのマージン
type CameraListCopyType = CameraDetail & {
  isDummy?: boolean
  has_data?: boolean
}

type VideoWrapStyleType = {
  width: string
  height: string
  marginRight: string
  marginTop: string
}

type PropsType = {
  cameraList: CameraDetail[]
  division: number
  paused: boolean
  showNav: boolean
  speed: number
  displayDate?: string | null
  useLive?: boolean
  archiveDisplayTime?: number
  streamArchiveDate?: string | null
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Videos',
  props: {
    cameraList: null,
    division: null,
    paused: { type: Boolean },
    showNav: { type: Boolean },
    speed: null,
    displayDate: { default: null },
    useLive: { type: Boolean, default: true },
    archiveDisplayTime: { default: 0 },
    streamArchiveDate: { default: null }
  },
  emits: ["loadeddata", "retryRequestArchive"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'loadeddata', data: { videoElm: HTMLMediaElement; hasLoadedVideo: boolean }): void
  (e: 'retryRequestArchive'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { cameraList: CameraDetail[], division: number, paused: boolean, showNav: boolean, speed: number, displayDate: string | null, useLive: boolean, archiveDisplayTime: number, streamArchiveDate: string | null };

const videoMargin = 4

const router = useRouter()

const videos = ref<HTMLMediaElement>()
const videosWidth = ref<null | number>(null)
const videosHeight = ref<null | number>(null)
const loadedCount = ref<number>(0)
const errorCount = ref<number>(0)
const videoElms = ref<HTMLMediaElement[]>([])
const cameraListCopy = ref<CameraListCopyType[]>([])





onMounted(() => {
  // カメラデータをコピーする
  setCameraCopyData()

  // コンテンツの幅、高さをセット(mounted時はスクロールバーの幅が入ってしまいずれるので$nextTickを利用する)
  nextTick(() => {
    setContentSize()
  })
})

onUnmounted(() => {
  // LIVE映像のリクエスト配列をリセット
  live.resetRequest()
})

const videoWrapStyle = computed<(index: number) => VideoWrapStyleType>(() => {
  return (index: number) => {
    // コンテンツの幅と、分割数によってボックスのサイズを設定する
    const row = APP.MULTI_VIDEO_DIVISION_OPTIONS.find((d) => d.label === props.division).row

    // 横一列の数
    const columns = props.division / row
    // 一列のマージンの合計
    const totalMargin = (columns - 1) * videoMargin
    // videoの右マージン
    const marginRight = index % columns === columns - 1 ? 0 : videoMargin
    // videoの上マージン
    const marginTop = Math.floor(index / columns) === 0 ? 0 : videoMargin

    // 高さ
    const height = Math.floor((videosHeight.value - videoMargin * (row - 1)) / row)
    // 幅
    // TODO - 1 / columnsは幅が収まらず、レイアウトが崩れてしまう場合があるので追加
    const width = (videosWidth.value - totalMargin) / columns - 1 / columns
    return {
      width: `${width}px`,
      height: `${height}px`,
      marginRight: `${marginRight}px`,
      marginTop: `${marginTop}px`,
    }
  }
})

const videosStyle = computed<{ height: string }>(() => {
  return {
    height: `${videosHeight.value}px`,
  }
})

const liveUpdateInterval = computed<number>(() => {
  const len = cameraListCopy.value.filter((d) => !d.use_nolive && !d.isDummy).length
  return 1000 / len
})

// データがあるカメラデータ数
const hasDataCameraLength = computed<number>(() => {
  return props.cameraList.filter((d) => d.has_data === true).length
})

const setCameraCopyData = () => {
  // cameraListがmountedのタイミングで必ず存在する
  cameraListCopy.value = deepCopy(props.cameraList)

  // カメラのデータ数より分割数が大きい場合はレイアウト崩れ対策でダミーのデータを入れる
  if (cameraListCopy.value.length < props.division) {
    const dummyData = range(props.division - cameraListCopy.value.length).map(() => {
      return { isDummy: true, has_data: false }
    })
    cameraListCopy.value = cameraListCopy.value.concat(dummyData)
  }
}

const loadedData = (e: HTMLMediaElement) => {
  videoElms.value.push(e.target)
  loadedCount.value++
}

const loadedError = (camera: CameraDetail, index: number) => {
  loadedCount.value++
  errorCount.value++
  // 映像がloadできない場合はデータなしとして扱う
  camera.has_data = false
  camera.updateHasDataFlag = true
  cameraListCopy.value[index] = camera

  // エラーの数が有効なカメラ数と同じ場合は過去映像の再読み込みを行う(AWS署名の有効期限切れ対応)
  if (errorCount.value === hasDataCameraLength.value) {
    emit('retryRequestArchive')
  }
}

/**
 * 再生速度の変更
 */
const changePlaySpeed = () => {
  videoElms.value.forEach((d: HTMLMediaElement) => {
    d.playbackRate = props.speed
    d.defaultPlaybackRate = props.speed
  })
}

/**
 * 詳細ページを表示
 */
const showDetailPage = (code: string) => {
  const param = {
    name: 'detail',
    params: { code: code },
  }

  // 過去映像を表示時は表示時刻を連携する
  if (!props.useLive) {
    param.query = { date: props.displayDate }
  }

  // 設定の日付を連携する
  router.push(param)
}

/**
 * コンテンツの高さ、幅をセット
 */
const setContentSize = () => {
  if (!videos.value) {
    return
  }
  videosWidth.value = videos.value.clientWidth
  // -24は下部にマージンを空ける為
  videosHeight.value = window.innerHeight - videos.value.getBoundingClientRect().top - 24
}

const resize = () => {
  // リサイズ時にelementの高さと、幅を再設定
  setContentSize()
}

const resetArchives = () => {
  videoElms.value = []
  loadedCount.value = 0
  errorCount.value = 0
  // has_dataプロパティを書き換えているので、オリジナルのデータ初期化し直す
  setCameraCopyData()
}

/**
 * 映像の表示サイズ
 * @param {Number} index 映像の表示順
 * @param {Object} cameraDetail 映像の詳細情報
 * @return {Object} 映像の表示する幅、高さ
 */
const getDisplaySize = (index: number, cameraDetail: CameraDetail) => {
  const width = parseInt(videoWrapStyle.value(index).width)
  const height = parseInt(videoWrapStyle.value(index).height)
  // 横幅を基準に縦横比を算出
  const wAspect = cameraDetail.image_rs_width / cameraDetail.image_rs_height

  // 横幅が指定のボックスサイズより小さい場合は高さを100%で表示する
  if (height * wAspect < width) {
    return {
      width: height * wAspect,
      height: height,
    }
  } else {
    // 横幅が指定のボックスサイズより大きい場合は幅を100%で表示する
    // 高さを基準に縦横比を算出
    const hAspect = cameraDetail.image_rs_height / cameraDetail.image_rs_width
    return {
      width: width,
      height: width * hAspect,
    }
  }
}

watch(
  () => props.cameraList,
  () => {
    // 過去映像のデータが更新されたタイミングで初期化
    resetArchives()
  }
)
watch(
  () => props.useLive,
  (v) => {
    // ライブ映像に切り替えたタイミングで、過去映像の表示設定を初期化
    if (v) {
      resetArchives()
    }
  }
)

watch(loadedCount, (v) => {
  // videoのloadedの完了をカウントして全てのvideoがロード完了したのを判定する
  if (v === hasDataCameraLength.value && videoElms.value.length > 0) {
    const hasLoadedVideo = errorCount.value !== hasDataCameraLength.value
    // 再生速度をセット
    changePlaySpeed()
    // 全てのロード（エラー）が完了したら、再生基準にするエレメントを連携する
    emit('loadeddata', {
      videoElm: videoElms.value[videoElms.value.length - 1],
      // 1つでもロードに成功しているかのフラグ
      hasLoadedVideo: hasLoadedVideo,
    })
  }
})
watch(
  () => props.paused,
  (v) => {
    // アクティブなカメラを全て再生、停止する
    videoElms.value.forEach((d: HTMLMediaElement) => {
      if (v) {
        d.pause()
      } else {
        d.play()
      }
    })
  }
)

watch(
  () => props.speed,
  () => {
    changePlaySpeed()
  }
)

watch(
  () => props.archiveDisplayTime,
  (v) => {
    // seekBarが操作された場合に映像の表示時間を変更
    videoElms.value.forEach((video) => {
      video.currentTime = v
    })
  }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(ResizeBox), { onResize: resize }, {
    default: _withCtx(() => [
      _createElementVNode("div", {
        class: "videos",
        ref_key: "videos",
        ref: videos,
        style: _normalizeStyle(_unref(videosStyle))
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(cameraListCopy.value, (camera, index) => {
          return (_openBlock(), _createElementBlock("div", {
            ref_for: true,
            ref: "videoWrap",
            key: index,
            style: _normalizeStyle(_unref(videoWrapStyle)(index)),
            class: "video_wrap"
          }, [
            (camera.isDummy)
              ? (_openBlock(), _createElementBlock("div", _hoisted_1, _hoisted_3))
              : (camera.use_time_lapse)
                ? (_openBlock(), _createBlock(_unref(MultiVideoTimelapse), {
                    key: 1,
                    "use-live": __props.useLive,
                    "camera-detail": camera,
                    "display-size": getDisplaySize(index, camera)
                  }, null, 8, ["use-live", "camera-detail", "display-size"]))
                : (camera.use_streaming)
                  ? (_openBlock(), _createBlock(_unref(MultiVideoStream), {
                      key: 2,
                      "camera-detail": camera,
                      "use-live": __props.useLive,
                      "display-size": getDisplaySize(index, camera),
                      "stream-archive-date": __props.streamArchiveDate,
                      onLoadedData: loadedData,
                      onLoadedError: ($event: any) => (loadedError(camera, index))
                    }, null, 8, ["camera-detail", "use-live", "display-size", "stream-archive-date", "onLoadedError"]))
                  : (__props.useLive)
                    ? (_openBlock(), _createBlock(_unref(MultiVideoLive), {
                        key: 3,
                        "camera-detail": camera,
                        "update-interval": _unref(liveUpdateInterval),
                        "display-size": getDisplaySize(index, camera)
                      }, null, 8, ["camera-detail", "update-interval", "display-size"]))
                    : (_openBlock(), _createBlock(_unref(MultiVideoArchive), {
                        key: 4,
                        "camera-detail": camera,
                        onLoadedData: loadedData,
                        onLoadedError: ($event: any) => (loadedError(camera, index))
                      }, null, 8, ["camera-detail", "onLoadedError"])),
            _createVNode(_Transition, null, {
              default: _withCtx(() => [
                _withDirectives(_createElementVNode("span", {
                  onClick: ($event: any) => (showDetailPage(camera.camera_code)),
                  class: "video_name link_scale_s"
                }, [
                  _createElementVNode("span", null, _toDisplayString(camera.camera_name), 1),
                  _createVNode(_unref(Icon), {
                    size: "22",
                    type: "keyboard_arrow_right"
                  })
                ], 8, _hoisted_4), [
                  [_vShow, __props.showNav && !camera.isDummy]
                ])
              ]),
              _: 2
            }, 1024)
          ], 4))
        }), 128))
      ], 4)
    ]),
    _: 1
  }))
}
}

})